import { useState, useMemo, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Zap, BookOpen, RotateCcw } from "lucide-react";
import { TeacherSubject } from "@/hooks/useTeacherSubjects";
import { useToast } from "@/hooks/use-toast";

interface SubjectSelection {
  subjectId: string;
  subject: string;
  courseType: 'Regular' | 'Inter';
  addPrimaryLower: boolean;
  addPrimaryUpper: boolean;
}

interface QuickAddPrimarySubjectsModalProps {
  open: boolean;
  onClose: () => void;
  primarySubjects: TeacherSubject[];
  existingSubjects: TeacherSubject[];
  onAddSubjects: (subjects: Omit<TeacherSubject, 'id' | 'created_at' | 'updated_at'>[]) => Promise<boolean>;
  teacherCode: string;
}

export function QuickAddPrimarySubjectsModal({
  open,
  onClose,
  primarySubjects,
  existingSubjects,
  onAddSubjects,
  teacherCode,
}: QuickAddPrimarySubjectsModalProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize selections
  const [selections, setSelections] = useState<Record<string, SubjectSelection>>({});

  // Reinitialize selections when primarySubjects or open state changes
  useEffect(() => {
    if (open && primarySubjects.length > 0) {
      const initial: Record<string, SubjectSelection> = {};
      primarySubjects.forEach(subject => {
        initial[subject.id] = {
          subjectId: subject.id,
          subject: subject.subject,
          courseType: subject.course_type,
          addPrimaryLower: false,
          addPrimaryUpper: false,
        };
      });
      setSelections(initial);
    }
  }, [open, primarySubjects]);

  // Check if a subject already exists
  const isDuplicate = (subject: string, gradeLevel: string, courseType: string) => {
    return existingSubjects.some(
      s => s.subject === subject && s.grade_level === gradeLevel && s.course_type === courseType
    );
  };

  // Count subjects to be added
  const subjectsToAddCount = useMemo(() => {
    let count = 0;
    Object.values(selections).forEach(selection => {
      if (!selection) return; // Guard against undefined
      if (selection.addPrimaryLower && !isDuplicate(selection.subject, 'ประถมต้น', selection.courseType)) {
        count++;
      }
      if (selection.addPrimaryUpper && !isDuplicate(selection.subject, 'ประถมปลาย', selection.courseType)) {
        count++;
      }
    });
    return count;
  }, [selections, existingSubjects]);

  // Toggle selection
  const toggleSelection = (subjectId: string, type: 'lower' | 'upper') => {
    setSelections(prev => {
      const current = prev[subjectId];
      if (!current) return prev; // Guard against undefined
      
      return {
        ...prev,
        [subjectId]: {
          ...current,
          addPrimaryLower: type === 'lower' ? !current.addPrimaryLower : current.addPrimaryLower,
          addPrimaryUpper: type === 'upper' ? !current.addPrimaryUpper : current.addPrimaryUpper,
        }
      };
    });
  };

  // Quick select all lower or upper
  const selectAll = (type: 'lower' | 'upper') => {
    setSelections(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (type === 'lower') {
          updated[key].addPrimaryLower = true;
        } else {
          updated[key].addPrimaryUpper = true;
        }
      });
      return updated;
    });
  };

  // Reset all selections
  const resetAll = () => {
    setSelections(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        updated[key].addPrimaryLower = false;
        updated[key].addPrimaryUpper = false;
      });
      return updated;
    });
  };

  // Handle submit
  const handleSubmit = async () => {
    const subjectsToAdd: Omit<TeacherSubject, 'id' | 'created_at' | 'updated_at'>[] = [];

    Object.values(selections).forEach(selection => {
      if (!selection) return; // Guard against undefined
      
      if (selection.addPrimaryLower && !isDuplicate(selection.subject, 'ประถมต้น', selection.courseType)) {
        subjectsToAdd.push({
          teacher_code: teacherCode,
          subject: selection.subject,
          grade_level: 'ประถมต้น',
          course_type: selection.courseType,
        });
      }
      if (selection.addPrimaryUpper && !isDuplicate(selection.subject, 'ประถมปลาย', selection.courseType)) {
        subjectsToAdd.push({
          teacher_code: teacherCode,
          subject: selection.subject,
          grade_level: 'ประถมปลาย',
          course_type: selection.courseType,
        });
      }
    });

    if (subjectsToAdd.length === 0) {
      toast({
        title: 'กรุณาเลือกวิชา',
        description: 'กรุณาเลือกอย่างน้อย 1 วิชาที่ต้องการเพิ่ม',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    const success = await onAddSubjects(subjectsToAdd);
    setIsSubmitting(false);

    if (success) {
      toast({
        title: 'เพิ่มวิชาสำเร็จ',
        description: `เพิ่ม ${subjectsToAdd.length} วิชาเรียบร้อยแล้ว`,
      });
      onClose();
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-xl">
            <Zap className="w-6 h-6 text-orange-600" />
            เพิ่มวิชาระดับประถมใหม่อย่างรวดเร็ว
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left space-y-4">
            <p className="text-foreground">
              เลือกระดับชั้นที่ท่านต้องการเพิ่มสำหรับแต่ละวิชา:
            </p>

            {/* Quick select buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => selectAll('lower')}
                className="text-xs"
              >
                ✓ เลือกประถมต้นทั้งหมด
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => selectAll('upper')}
                className="text-xs"
              >
                ✓ เลือกประถมปลายทั้งหมด
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={resetAll}
                className="text-xs text-red-600 border-red-200 hover:bg-red-50"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                รีเซ็ต
              </Button>
            </div>

            {/* Subject list */}
            <div className="space-y-3">
              {primarySubjects.map((subject) => {
                const selection = selections[subject.id];
                const lowerExists = isDuplicate(subject.subject, 'ประถมต้น', subject.course_type);
                const upperExists = isDuplicate(subject.subject, 'ประถมปลาย', subject.course_type);

                return (
                  <div
                    key={subject.id}
                    className="border border-border rounded-lg p-4 bg-card space-y-2"
                  >
                    <div className="flex items-center gap-2 font-medium">
                      <BookOpen className="w-4 h-4 text-primary" />
                      <span>{subject.subject}</span>
                      <span className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground">
                        {subject.course_type}
                      </span>
                    </div>
                    
                    <div className="pl-6 space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`${subject.id}-lower`}
                          checked={selection?.addPrimaryLower || false}
                          onCheckedChange={() => toggleSelection(subject.id, 'lower')}
                          disabled={lowerExists}
                        />
                        <Label
                          htmlFor={`${subject.id}-lower`}
                          className={`text-sm ${lowerExists ? 'text-muted-foreground line-through' : ''}`}
                        >
                          ประถมต้น (ป.1-ป.3)
                          {lowerExists && <span className="ml-2 text-xs">(มีอยู่แล้ว)</span>}
                        </Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`${subject.id}-upper`}
                          checked={selection?.addPrimaryUpper || false}
                          onCheckedChange={() => toggleSelection(subject.id, 'upper')}
                          disabled={upperExists}
                        />
                        <Label
                          htmlFor={`${subject.id}-upper`}
                          className={`text-sm ${upperExists ? 'text-muted-foreground line-through' : ''}`}
                        >
                          ประถมปลาย (ป.4-ป.6)
                          {upperExists && <span className="ml-2 text-xs">(มีอยู่แล้ว)</span>}
                        </Label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {subjectsToAddCount > 0 && (
              <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
                <AlertDescription className="text-green-800 dark:text-green-200">
                  จะเพิ่ม <strong>{subjectsToAddCount}</strong> วิชาใหม่
                </AlertDescription>
              </Alert>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || subjectsToAddCount === 0}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                กำลังเพิ่ม...
              </>
            ) : (
              `เพิ่มวิชาที่เลือก (${subjectsToAddCount})`
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
